import { useEffect, useRef } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { useProfileEvents } from './useProfileEvents';

/**
 * Hook that observes canvas store changes and logs profile events.
 * Placed in the Canvas page to track:
 * - Board opened/closed with duration
 * - Canvas session duration
 * - Item creation events
 *
 * Uses vanilla Zustand subscribe (no selector middleware needed).
 */
export function useCanvasEventLogger() {
  const { logBoardOpened, logBoardClosed, logCanvasSession, logItemCreated } =
    useProfileEvents();

  const boardOpenTimeRef = useRef<{ boardId: string; openedAt: number } | null>(
    null
  );
  const sessionStartRef = useRef<number>(Date.now());
  const itemsMovedRef = useRef(0);
  const zoomRangeRef = useRef<[number, number]>([1, 1]);
  const prevBoardIdRef = useRef<string | null>(null);
  const prevItemCountRef = useRef<number | null>(null);

  // Track board open/close + item creation + zoom via single subscription
  useEffect(() => {
    // Initialize with current state
    const initialState = useCanvasStore.getState();
    prevBoardIdRef.current = initialState.currentBoardId;
    prevItemCountRef.current = initialState.items.length;
    zoomRangeRef.current = [initialState.zoom, initialState.zoom];

    if (initialState.currentBoardId) {
      logBoardOpened(initialState.currentBoardId);
      boardOpenTimeRef.current = {
        boardId: initialState.currentBoardId,
        openedAt: Date.now(),
      };
    }

    const unsubscribe = useCanvasStore.subscribe((state) => {
      // Board change detection
      if (state.currentBoardId !== prevBoardIdRef.current) {
        // Close previous board
        if (
          boardOpenTimeRef.current &&
          boardOpenTimeRef.current.boardId !== state.currentBoardId
        ) {
          const duration = Math.round(
            (Date.now() - boardOpenTimeRef.current.openedAt) / 1000
          );
          if (duration > 2) {
            logBoardClosed(boardOpenTimeRef.current.boardId, duration);
          }
          boardOpenTimeRef.current = null;
        }

        // Open new board
        if (state.currentBoardId) {
          logBoardOpened(state.currentBoardId);
          boardOpenTimeRef.current = {
            boardId: state.currentBoardId,
            openedAt: Date.now(),
          };
        }

        prevBoardIdRef.current = state.currentBoardId;
      }

      // Item creation detection
      const currentCount = state.items.length;
      if (
        prevItemCountRef.current !== null &&
        currentCount > prevItemCountRef.current
      ) {
        const newItem = state.items[state.items.length - 1];
        if (newItem) {
          logItemCreated(newItem.item_type, newItem.board_id);
        }
      }
      prevItemCountRef.current = currentCount;

      // Zoom range tracking
      if (state.zoom < zoomRangeRef.current[0])
        zoomRangeRef.current[0] = state.zoom;
      if (state.zoom > zoomRangeRef.current[1])
        zoomRangeRef.current[1] = state.zoom;
    });

    return unsubscribe;
  }, [logBoardOpened, logBoardClosed, logItemCreated]);

  // Log canvas session on unmount
  useEffect(() => {
    return () => {
      const duration = Math.round(
        (Date.now() - sessionStartRef.current) / 1000
      );
      if (duration > 10) {
        logCanvasSession(
          duration,
          itemsMovedRef.current,
          zoomRangeRef.current
        );
      }

      // Close any open board
      if (boardOpenTimeRef.current) {
        const boardDuration = Math.round(
          (Date.now() - boardOpenTimeRef.current.openedAt) / 1000
        );
        if (boardDuration > 2) {
          logBoardClosed(boardOpenTimeRef.current.boardId, boardDuration);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
